import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useQuoteBuilderApi } from '../api/client'
import { PageState } from '../ui/page'
import { ProductFilters } from '../catalog/product-filters'
import {
  activeFilterCount,
  emptyProductFilters,
  productFilterSearch,
  type ProductFilterState,
} from '../catalog/filter-state'
import {
  equipmentCatalogSearch,
  manufacturersQueryOptions,
  productsQueryOptions,
  type ProductFacets,
  type ProductSort,
  type ProductSummary,
  type QuoteOnlyProduct,
} from '../catalog/queries'
import { CatalogResultCard } from '../catalog/result-card'
import { EditorDrawer } from './drawer'
import { SearchInput } from '@/components/search-input'

const sortLabels: Record<ProductSort, string> = {
  featured: 'Featured',
  relevance: 'Best match',
  newest: 'Newest',
  price_asc: 'Price: low to high',
  price_desc: 'Price: high to low',
}

/** Search the live catalog and pick a product (demo `ProductDrawer`); also the Replace picker. */
export function CatalogDrawer({
  open,
  onOpenChange,
  onSelect,
  busy,
  title = 'Add from catalog',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (product: ProductSummary | QuoteOnlyProduct) => void
  busy: boolean
  title?: string
}) {
  const api = useQuoteBuilderApi()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ProductSort>(equipmentCatalogSearch.sort ?? 'featured')
  const [filters, setFilters] = useState<ProductFilterState>(emptyProductFilters)
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
      setPage(1)
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [query])
  const products = useQuery({
    ...productsQueryOptions(api, {
      ...equipmentCatalogSearch,
      query: debouncedQuery || undefined,
      ...productFilterSearch(filters),
      sort,
      page,
    }),
    enabled: open,
  })
  const manufacturers = useQuery({ ...manufacturersQueryOptions(api), enabled: open })
  const pages = products.data?.pages ?? 0
  // The opening browse carries no filter lists; offer every live manufacturer there.
  const facets: ProductFacets | undefined =
    products.data && !products.data.facets.manufacturers.length && manufacturers.data
      ? {
          ...products.data.facets,
          manufacturers: manufacturers.data.items
            .filter((item) => item.product_count > 0)
            .map((item) => ({ value: item.name, label: item.name, count: item.product_count })),
        }
      : products.data?.facets
  const filterCount = activeFilterCount(filters)
  const changeFilters = (value: ProductFilterState) => {
    setFilters(value)
    setPage(1)
  }
  const action = title.startsWith('Replace ') ? 'Replace' : 'Project'
  // Replacing keeps catalog pricing, so a plan priced on each quote is never offered there.
  const quoteOnly = (products.data?.quote_only?.items ?? []).filter(
    (product) => action === 'Project' || !product.priced_on_each_quote,
  )

  return (
    <EditorDrawer onOpenChange={onOpenChange} open={open} title={title} wide>
      <div className="grid shrink-0 gap-2 border-b p-3 sm:grid-cols-[minmax(0,1fr)_200px]">
        <label className="relative block min-w-0">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <span className="sr-only">Search catalog</span>
          <SearchInput
            autoFocus
            className="pl-9"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search model, product, manufacturer"
            type="search"
            value={query}
          />
        </label>
        <Select
          onValueChange={(value) => {
            setSort(value as ProductSort)
            setPage(1)
          }}
          value={sort}
        >
          <SelectTrigger aria-label="Sort catalog products" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(sortLabels) as ProductSort[]).map((value) => (
              <SelectItem key={value} value={value}>
                {sortLabels[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <details className="max-h-[55vh] shrink-0 overflow-y-auto border-b bg-muted/30 px-3 py-2 sm:hidden">
        <summary className="flex min-h-9 cursor-pointer list-none items-center justify-between text-sm font-medium [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-2">
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            Filters
          </span>
          <span className="tabular-nums text-muted-foreground">{filterCount || ''}</span>
        </summary>
        <ProductFilters available={facets} className="pb-2 pt-3" onChange={changeFilters} value={filters} />
      </details>
      <div className="grid min-h-0 flex-1 sm:grid-cols-[220px_minmax(0,1fr)]">
        <aside aria-label="Catalog filters" className="hidden overflow-y-auto border-r bg-muted/30 p-3 sm:block">
          <ProductFilters available={facets} onChange={changeFilters} value={filters} />
        </aside>
        <div className="min-h-0 overflow-y-auto p-3">
          <div className="mb-2 flex min-h-6 items-center justify-between text-xs text-muted-foreground" role="status">
            <span>
              {products.isFetching ? 'Updating...' : `${(products.data?.total ?? 0).toLocaleString()} products`}
            </span>
            <span>{pages ? `Page ${page} of ${pages.toLocaleString()}` : ''}</span>
          </div>
          {products.isLoading ? <PageState compact kind="loading" title="Searching catalog" /> : null}
          {products.isError ? (
            <PageState
              action={
                <Button onClick={() => void products.refetch()} size="sm" variant="outline">
                  Try again
                </Button>
              }
              compact
              kind="error"
              title="Catalog unavailable"
            />
          ) : null}
          {quoteOnly.length ? (
            <section aria-labelledby="catalog-quote-only" className="mb-4 grid gap-2">
              <div className="flex items-baseline justify-between text-xs">
                <h3 className="text-sm font-semibold" id="catalog-quote-only">
                  Quote only
                </h3>
                <span className="text-muted-foreground">
                  {(products.data?.quote_only?.total ?? quoteOnly.length).toLocaleString()} matching
                </span>
              </div>
              {quoteOnly.map((product) => (
                <CatalogResultCard
                  action={action}
                  busy={busy}
                  key={product.id}
                  note={
                    product.priced_on_each_quote
                      ? 'Priced on each quote'
                      : product.bought_through
                        ? `Bought through ${product.bought_through}`
                        : null
                  }
                  onSelect={() => onSelect(product)}
                  product={product}
                />
              ))}
            </section>
          ) : null}
          {!products.isLoading && !products.isError && !products.data?.items.length && !quoteOnly.length ? (
            <PageState
              compact
              description="Change the search or remove a filter."
              kind="empty"
              title="No matching products"
            />
          ) : null}
          <div className="grid gap-2">
            {products.data?.items.map((product) => (
              <CatalogResultCard action={action} busy={busy} key={product.id} onSelect={onSelect} product={product} />
            ))}
          </div>
          {pages > 1 ? (
            <div className="mt-3 flex items-center justify-end gap-1 border-t pt-3">
              <Button
                aria-label="Previous catalog page"
                disabled={page <= 1 || products.isFetching}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                size="icon"
                variant="outline"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                aria-label="Next catalog page"
                disabled={page >= pages || products.isFetching}
                onClick={() => setPage((value) => Math.min(pages, value + 1))}
                size="icon"
                variant="outline"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </EditorDrawer>
  )
}
